import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './Assignment.module.scss';
import { useTranslation } from 'react-i18next';
import Avatar from '../Avatar/Avatar';
import cx from 'classnames';
import { CustomButton } from '../Button/Button';
import Tooltip from '../Tooltip/Tooltip';
import { OptionsMenu } from '../OptionsMenu/OptionsMenu';
import useFixedCardMenu from 'engage/features/Editor/hooks/useFixedCardMenu';

// Story status constants
const ASSIGNMENT_STATUS = {
  PENDING: 0,
  PROCESSING: 1,
  READY: 2,
  FAILED: 3,
  EXPIRED: 4,
};

const Assignment = ({ assignment, handleAssignmentClick }) => {
  const { t } = useTranslation();

  const wrapperRef = useRef(null);

  const [isHover, setIsHover] = useState(false);

  const { menuRef, showMenu, menuCoordinates, onOpenMenu, toggleShowMenu } = useFixedCardMenu();

  const isMobile = window.matchMedia('(max-width: 900px)')?.matches;

  const handleCardClick = useCallback((e) => {
    if (isMobile) {
      e.stopPropagation();
      handleAssignmentClick(assignment);
    }
  }, [isMobile, assignment, handleAssignmentClick])

  const handleSelection = useCallback((option) => {
    toggleShowMenu();
    handleAssignmentClick(assignment, option.id === 'open');
  }, [assignment, handleAssignmentClick]);

  const navigationOptions = useMemo(() => [
    {
      id: 'edit',
      label: t('home_page.assignments.work_on'),
      disabled: false,
    },
    {
      id: 'open',
      label: t('home_page.assignments.view'),
      disabled: assignment.storyStatus !== ASSIGNMENT_STATUS.READY,
      tooltip: t('home_page.assignments.incomplete_tooltip'),
    },
  ], [assignment, t]);

  return (
    <div
      className={cx(styles.assignmentItem, { [styles.animate]: assignment.readAt === null })}
      ref={wrapperRef}
      onClick={handleCardClick}
    >
      <div className={styles.assignmentInfo}>
        <Avatar image={assignment.inviterAvatar} title={assignment.inviterName} alt={`Video's owner avatar`} />
        <div className={styles.assignmentDetailInfo}>
          <span className={styles.assignmentName} onMouseOver={() => setIsHover(true)} onMouseOut={() => setIsHover(false)}>{assignment.assignmentName}</span>
          <span className={styles.assignmentInviterName}>{t('my_video.by')}: {assignment.inviterName}</span>
          {assignment.expirationDate && (
            <div className={styles.expirationDateContainer}>
              <span className={styles.expirationText}>{t('home_page.expiration_date')}:</span>
              <span
                className={cx(styles.expirationDate, {
                  [styles.expirationDateRed]: new Date() > new Date(assignment.expirationDate),
                })}
              >
                {t('general.date', { date: new Date(assignment.expirationDate) })}
              </span>
            </div>
          )}
          <div className={styles.additionalInfoContainer}>
            {assignment.readAt === null && <div className={cx(styles.labelContainer, styles.newContainer)}>{t('home_page.new')}</div>}
            {assignment.completedAt && (
              <div className={cx(styles.labelContainer, styles.doneContainer)}>{t('clip_organizer.assignments.done')}</div>
            )}
            <span className={styles.creationTime}>{assignment.updatedTime}</span>
          </div>
        </div>
      </div>
      {!isMobile && <div className={styles.buttonContainer}>
        <CustomButton className={styles.openBtn} variant='primary' label={t('home_page.assignments.open')} onClick={(e) => onOpenMenu({ id: assignment.storyId, clientX: e.clientX, clientY: e.clientY })} />
      </div>}
      {showMenu.flag && <OptionsMenu menuRef={menuRef} className={styles.menu} options={navigationOptions} onSelect={handleSelection} customStyles={{ top: menuCoordinates.y, left: menuCoordinates.x }} topOffset={20} />}
      <Tooltip text={assignment.assignmentName} isHover={isHover} wrapperRef={wrapperRef} position='middle' x_offset={10} classNameTooltip={styles.tooltip} />
    </div>
  );
};

export default Assignment;
